/**
 * 业务标识配置管理 Hook
 */

import { useState, useEffect } from "react";
import type { BusinessKey, ReceiverInfo } from "../types";
import { createPlatformAdapter } from "../adapters/PlatformFactory";
import { getReceiversByBizKeyPrefix } from "../services/api";

export function useBusinessKeys(isReady: boolean = true) {
  const [businessKeys, setBusinessKeys] = useState<BusinessKey[]>([]);
  const [selectedBusinessKey, setSelectedBusinessKey] =
    useState<BusinessKey | null>(null);
  const [receivers, setReceivers] = useState<ReceiverInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const platform = createPlatformAdapter();

  /**
   * 加载业务标识列表
   */
  const loadBusinessKeys = async () => {
    setLoading(true);
    setError(null);

    try {
      const configTable = await platform.getTableByName("业务标识配置表");

      const records = await platform.getRecords(configTable, {
        pageSize: 5000,
      });

      const keys: BusinessKey[] = records.map((record) => {
        const key = {
          name: String(record.fields["业务标识名称"] || ""),
          bizKeyPrefix: String(record.fields["biz_key前缀"] || ""),
          description: String(record.fields["描述"] || ""),
        };
        return key;
      });

      setBusinessKeys(keys);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "加载业务标识失败";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 选择业务标识并加载对应的消息接收者
   */
  const selectBusinessKey = async (businessKey: BusinessKey) => {
    setSelectedBusinessKey(businessKey);
    setLoading(true);
    setError(null);

    try {
      // 调用后端 API 获取消息接收者列表
      const receiverList = await getReceiversByBizKeyPrefix(
        businessKey.bizKeyPrefix
      );
      setReceivers(receiverList);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "获取消息接收者失败";
      setError(errorMessage);
      setReceivers([]);
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时加载业务标识列表（等待权限就绪）
  useEffect(() => {
    if (isReady) {
      loadBusinessKeys();
    }
  }, [isReady]);

  return {
    businessKeys,
    selectedBusinessKey,
    receivers,
    loading,
    error,
    selectBusinessKey,
    reloadBusinessKeys: loadBusinessKeys,
  };
}
